import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AuthPages.css';
import { siteName } from '../utils/adminSite';

// Gymnasts without their own account sign in with their name and a 6-digit
// code: a parent's share code or the club's code of the day.
const ChildLogin = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', accessCode: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState(null); // gymnasts sharing the name
  const navigate = useNavigate();
  const { childLogin, childLoginDisambiguate } = useAuth();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const finish = (result) => {
    if (result.success) navigate('/child');
    else if (result.needsDisambiguation) setMatches(result.gymnasts);
    else setError(result.error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMatches(null);
    try {
      finish(await childLogin(formData.firstName.trim(), formData.lastName.trim(), formData.accessCode.trim()));
    } catch {
      setError('Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const choose = async (gymnastId) => {
    setLoading(true);
    setError(null);
    try {
      finish(await childLoginDisambiguate(gymnastId, formData.accessCode.trim()));
    } catch {
      setError('Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (matches) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">{siteName()}</div>
          <h1 className="auth-heading">Which one are you?</h1>
          <p className="auth-hint">More than one gymnast is called {formData.firstName} {formData.lastName}.</p>
          {error && <p className="auth-error">{error}</p>}
          <div className="auth-form">
            {matches.map(g => (
              <button key={g.id} type="button" className="auth-btn auth-btn--primary" disabled={loading} onClick={() => choose(g.id)}>
                {g.fullName}{g.club ? ` (${g.club})` : ''}
              </button>
            ))}
          </div>
          <div className="auth-links">
            <button type="button" className="auth-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
              onClick={() => { setMatches(null); setError(null); }}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">{siteName()}</div>
        <h1 className="auth-heading">Gymnast sign in</h1>
        <p className="auth-hint">Use your name and the 6-digit code from your parent or coach.</p>

        {error && <p className="auth-error">{error}</p>}

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label">First name
            <input name="firstName" value={formData.firstName} onChange={handleChange}
              className="auth-input" required autoComplete="given-name" />
          </label>
          <label className="auth-label">Last name
            <input name="lastName" value={formData.lastName} onChange={handleChange}
              className="auth-input" required autoComplete="family-name" />
          </label>
          <label className="auth-label">Code
            <input name="accessCode" value={formData.accessCode} onChange={handleChange}
              className="auth-input" required inputMode="numeric" autoComplete="off"
              maxLength={6} pattern="[0-9]{6}" title="The code is 6 numbers" />
          </label>
          <button type="submit" disabled={loading} className="auth-btn auth-btn--primary">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">Parent, coach or member sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default ChildLogin;
